import * as ngCore from '@angular/core'
import * as ec from '@attain-apps/fe/web/main/lib/entity-collection'
import { TypeAsserter } from '@attain-apps/type-guards'
import * as rxjs from 'rxjs'

import * as dms from '../../../dms'
import * as organizations from '../../organizations'

import * as gql from './gql'
import type { TypeParams } from './types'

/**
 * Record assigned organizations API, for fetching and updating space record assigned organizations
 */
@ngCore.Injectable()
export class Api implements ec.models.Api<TypeParams> {
  /** @see {@link dms.schema.Feature} */
  private readonly _schemaFeature = ngCore.inject(dms.schema.Feature)

  /** @see {@link organizations.Feature} */
  private readonly _organizationsFeature = ngCore.inject(organizations.Feature)

  /** @inheritdoc */
  public list = ec.models.Api.list.using.nothingButContext<TypeParams>(
    context => {
      if (!context) {
        return rxjs.of([])
      }

      return gql.queries.recordAssignedOrgs.executor(context)
    },
  )

  /** @inheritdoc */
  public create: ec.models.Api<TypeParams>['create'] = (context, insertion) => {
    if (!context) {
      // TODO: Probably throw an error
      return rxjs.of()
    }

    return gql.mutations.createOrgAssignment.executor({
      ...context,
      input: gql.inputs.orgAssignment.TRANSFORMER.getGqlFrom({
        ...insertion,
      }),
    })
  }

  /** @inheritdoc */
  public delete: ec.models.Api<TypeParams>['delete'] = (context, id) => {
    TypeAsserter.assertIsNotNullish(context)
    return gql.mutations.deleteOrgAssignment.executor({
      ...context,
      assignmentId: id,
    })
  }

  /** @inheritdoc */
  public getAttributes: ec.models.Api<TypeParams>['getAttributes'] = () =>
    this._schemaFeature.selectors.schema.asObservable
      .pipe(
        // NOTE: The schema starts as an empty placeholder, so wait for the
        // loaded one to avoid emitting columns without organization entities
        rxjs.filter(schema => schema.areModelsInitialized),
        rxjs.map(schema =>
          schema.getEntitiesInDimension(organizations.constants.DIMENSION_ID),
        ),
      )
      .pipe(
        rxjs.switchMap(entities =>
          entities.length > 0
            ? rxjs.combineLatest(
                entities.map(entity =>
                  this._organizationsFeature.selectors
                    .getOrganizationEc(entity.id)
                    .asObservable.pipe(
                      rxjs.switchMap(
                        orgEc =>
                          orgEc?.plugins.cachePlugin.feature.selectors.lookup
                            .asObservable ?? rxjs.of(undefined),
                      ),
                      rxjs.map(
                        lookup =>
                          ({
                            getValueFrom: ({ organizationLookup }) => {
                              const organizationId =
                                organizationLookup[entity.id]
                              return organizationId
                                ? lookup?.[organizationId]?.name
                                : undefined
                            },
                            id: entity.id,
                            name: entity.name,
                          }) satisfies ec.models.Attribute<TypeParams>,
                      ),
                    ),
                ),
              )
            : rxjs.of([]),
        ),
        rxjs.map(organizationAttrs => [
          ...organizationAttrs,
          ec.models.Attribute.fromKey<TypeParams>('isOccupier', {
            name: 'Is Occupier?',
          }),
        ]),
      )
}
